<template>
  <b-row v-if="eventTypes.length > 0">
    <b-col>
      <b-card>
        <b-card-actions
          ref="loadingCard"
          no-actions
        >
          <b-tabs>
            <b-tab
              v-for="eventType in eventTypes"
              :key="eventType.slug"
              :href="`#${eventType.slug}`"
              :active="isActive(eventType.slug)"
            >
              <template #title>
                <strong>
                  {{ eventType.description }}
                  <b-badge variant="primary">{{ getStatistic(eventType.slug, 'available') }}</b-badge>
                  <span class="ml-1 text-success">Done: {{ getStatistic(eventType.slug, 'done') }}</span>
                </strong>
              </template>
              <event-component
                :shipment-id="shipmentId"
                :type="eventType.slug"
                :event-type="eventType"
                :done="getStatistic(eventType.slug, 'done')"
              />
            </b-tab>
          </b-tabs>
        </b-card-actions>
      </b-card>
    </b-col>
  </b-row>
  <b-row v-else>
    <b-col>
      <b-card>
        <b-card-text
          class="text-center"
        >
          <b-spinner
            v-if="loading"
            class="mr-2"
          />
          {{ loading ? 'Loading event types...' : 'No event types configured for this company.' }}
        </b-card-text>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BBadge,
  BRow,
  BCol,
  BTabs,
  BTab,
  BCard,
  BCardText,
  BSpinner,
} from 'bootstrap-vue'
import shipmentModel from '@/views/shipments/shipmentModel'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import EventComponent from '@/views/shipments/events/EventComponent.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { EventBus } from '@core/utils/utils'

export default {
  components: {
    EventComponent,
    BBadge,
    BRow,
    BCol,
    BTabs,
    BTab,
    BCard,
    BCardActions,
    BCardText,
    BSpinner,
  },
  props: {
    shipmentId: {
      type: [String, Number],
      required: true,
    },
    tab: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      eventTypes: [],
      statistics: {},
      companyId: null,
    }
  },
  created() {
    EventBus.$on('refresh-shipment-statistics', this.updateStatistics)

    const channel = this.$pusher.subscribe(`shipment-${this.shipmentId}`)

    channel.unbind_all()

    channel.bind('declaration-status-sent', () => {
      this.updateStatistics()
    })

    channel.bind('refresh-statistics', () => {
      this.updateStatistics()
    })

    channel.bind('declaration-status', () => {
      this.updateStatistics()
      EventBus.$emit('general-declaration-update', this.tab)
    })

    channel.bind('event-processing', () => {
      this.updateStatistics(false)
      EventBus.$emit('shipment-event-processing', this.tab)
    })

    channel.bind('event-finished', () => {
      this.updateStatistics(false)
      EventBus.$emit('shipment-event-finished', this.tab)
    })
  },
  async mounted() {
    await this.getShipment()
    await this.loadEventTypes()
    await this.updateStatistics()
  },
  beforeDestroy() {
    EventBus.$off('refresh-shipment-statistics', this.updateStatistics)
  },
  methods: {
    async loadEventTypes() {
      try {
        const response = await this.$http.get(`/v1/api/companies/${this.companyId}/shipments/${this.shipmentId}/event-types`)

        this.eventTypes = response.data.data

        this.statistics = {}
        this.eventTypes.forEach(eventType => {
          this.statistics[eventType.slug] = { available: 0, done: 0 }
        })
      } catch {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error loading event types',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async getShipment() {
      const cachedData = shipmentModel().getCachedShipment(this.shipmentId)
      if (cachedData === null) {
        try {
          const response = await shipmentModel().getShipment(this.shipmentId, this)
          shipmentModel().cacheShipment(response.data)
          this.updatePageTitle(response.data.document_number)
          this.updateCompanyInfo(response.data.company)
          this.companyId = response.data.company_id
        } catch (e) {
          // handle error
        }
      } else {
        this.companyId = cachedData.company_id
        this.updatePageTitle(cachedData.document_number)
        this.updateCompanyInfo(cachedData.company)
      }
    },
    updatePageTitle(documentNumber) {
      this.$route.meta.pageTitle = `Shipment events (${documentNumber})`
      this.$globalEvent.$emit('update-page-title', this.$route.meta.pageTitle)
    },
    updateCompanyInfo(company) {
      if (this.$profile().data.user.companies.length > 1) {
        this.$route.meta.company = company
        this.$globalEvent.$emit('update-page-company', company)
      }
    },
    isActive(tab) {
      return this.tab === tab
    },
    getStatistic(eventTypeSlug, type) {
      const stat = this.statistics[eventTypeSlug] || this.statistics[eventTypeSlug.replace(/-/g, '_')]
      return stat?.[type] || 0
    },
    updateStatistics(showLoader = true) {
      if (this.$refs.loadingCard && showLoader) {
        this.$refs.loadingCard.showLoading = true
      }

      return this.$http.get(`/v1/api/companies/${this.companyId}/shipments/${this.shipmentId}/events/statistics`)
        .then(response => {
          if (this.$refs.loadingCard && showLoader) {
            this.$refs.loadingCard.showLoading = false
          }

          this.statistics = {}
          response.data.data.forEach(stat => {
            if (stat.event_type) {
              this.statistics[stat.event_type] = {
                available: stat.available,
                done: stat.done,
              }
            }
          })
        })
        .catch(() => {
          if (this.$refs.loadingCard && showLoader) {
            this.$refs.loadingCard.showLoading = false
          }
        })
    },
  },
}
</script>

<style scoped>

</style>
